import React, { useEffect, useState } from 'react';
import { getConsent, setConsent } from '../consent';

/** Every piece of text the banner shows. */
export interface CookieBannerLabels {
  /** Accessible name for the dialog. */
  dialogLabel: string;
  intro: string;
  policyLink: string;
  necessary: string;
  analytics: string;
  marketing: string;
  acceptAll: string;
  necessaryOnly: string;
  customise: string;
  saveChoice: string;
}

/**
 * English, matching i18next's `fallbackLng: 'en'`.
 *
 * An app that forgets to pass labels therefore degrades to the same language the rest of its UI
 * falls back to, rather than to a language the reader may not know. That was the bug: these strings
 * used to be hardcoded Romanian, so an English UI showed a Romanian consent dialog (#455).
 */
const DEFAULT_LABELS: CookieBannerLabels = {
  dialogLabel: 'Cookie consent',
  intro:
    'We use strictly necessary cookies to make this work. With your agreement we also use analytics and marketing cookies.',
  policyLink: 'Cookie policy',
  necessary: 'Strictly necessary (always on)',
  analytics: 'Analytics',
  marketing: 'Marketing',
  acceptAll: 'Accept all',
  necessaryOnly: 'Necessary only',
  customise: 'Customise',
  saveChoice: 'Save choice',
};

/** The label keys, in the order they appear in the banner. */
const LABEL_KEYS = Object.keys(DEFAULT_LABELS) as (keyof CookieBannerLabels)[];

/**
 * Where to send someone who wants to read the cookie policy before agreeing to anything.
 *
 * The banner's default href is the relative `/legal/cookies`, which is right for an app that serves
 * that page itself and a dead link for one that does not — and most of them do not. Rather than add
 * a legal-documents route to every portal, this points at the public site, which is where those
 * documents already live and are already kept current.
 *
 * The host is derived by dropping the first DNS label (`supplier.easydent365.easysoft.ro` →
 * `easydent365.easysoft.ro`), the same trick the docs app uses to find its sibling, so this needs no
 * build-time configuration and stays correct across staging and production. Falls back to the
 * relative path when the host has nothing to drop — localhost, an IP, the public site itself.
 */
export function cookiePolicyUrl(): string {
  if (typeof window === 'undefined') return '/legal/cookies';

  const parts = window.location.hostname.split('.');
  // Needs a label to drop and a real domain left over afterwards.
  if (parts.length < 3) return '/legal/cookies';

  return `https://${parts.slice(1).join('.')}/legal/cookies`;
}

/**
 * Builds the `labels` prop from an i18next-style `t`.
 *
 * Takes `t` as a plain function rather than calling `useTranslation` itself, so this package still
 * has no i18n dependency — see the note on `CookieBannerProps.labels`.
 *
 * A key that has no translation is **omitted**, not passed through. i18next returns the key itself
 * when a lookup misses, so passing the raw result would render `cookieBanner.intro` to a user;
 * omitting it lets the English default show instead. That is the whole point of the exercise.
 *
 * @param prefix key prefix — apps that keep these under a nested namespace pass their own path.
 */
export function cookieBannerLabels(
  t: (key: string, options?: { defaultValue?: string }) => string,
  prefix = 'cookieBanner',
): Partial<CookieBannerLabels> {
  const labels: Partial<CookieBannerLabels> = {};
  for (const key of LABEL_KEYS) {
    const value = t(`${prefix}.${key}`, { defaultValue: '' });
    if (value) labels[key] = value;
  }
  return labels;
}

interface CookieBannerProps {
  /** Link to the cookie policy page. */
  cookiePolicyHref?: string;
  /**
   * Translated text. Anything omitted falls back to English.
   *
   * Passed in rather than resolved here on purpose: this package is consumed by `operator-app` and
   * `supplier-portal`, neither of which depends on `react-i18next`. A `useTranslation` call in here
   * would be a module-level import of a package they do not have, breaking their builds — even
   * though neither renders this component. Keeping the design system free of i18n also leaves each
   * app owning its own namespaces, which differ (`common` in most, the default `translation` in
   * `public-app`).
   */
  labels?: Partial<CookieBannerLabels>;
}

/**
 * Cookie-consent banner. Shows until the visitor makes a choice. Non-essential categories
 * (analytics, marketing) default to OFF — accepting them is an explicit action, with equal
 * prominence given to "necessary only". Persists via the consent store, which the analytics
 * loader subscribes to.
 */
export const CookieBanner: React.FC<CookieBannerProps> = ({
  cookiePolicyHref = '/legal/cookies',
  labels,
}) => {
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const t: CookieBannerLabels = { ...DEFAULT_LABELS, ...labels };

  useEffect(() => {
    setVisible(getConsent() === null);
  }, []);

  if (!visible) return null;

  const save = (a: boolean, m: boolean) => {
    setConsent({ analytics: a, marketing: m });
    setVisible(false);
  };

  const checkbox = 'h-4 w-4 rounded border-line-strong text-brand-ink focus:ring-brand-500';

  return (
    <div
      role="dialog"
      aria-label={t.dialogLabel}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-line bg-surface px-4 py-4 shadow-lg"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-3">
        <p className="text-sm text-body-soft">
          {t.intro}{' '}
          <a href={cookiePolicyHref} className="text-brand-ink hover:underline">
            {t.policyLink}
          </a>
          .
        </p>

        {customize && (
          <div className="flex flex-col gap-2 text-sm text-body-soft">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked disabled className={checkbox} /> {t.necessary}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className={checkbox}
              />{' '}
              {t.analytics}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className={checkbox}
              />{' '}
              {t.marketing}
            </label>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => save(true, true)}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            {t.acceptAll}
          </button>
          <button
            onClick={() => save(false, false)}
            className="rounded-lg border border-line-strong px-4 py-2 text-sm font-medium text-body-soft hover:bg-canvas"
          >
            {t.necessaryOnly}
          </button>
          {!customize ? (
            <button
              onClick={() => setCustomize(true)}
              className="rounded-lg px-4 py-2 text-sm font-medium text-muted-strong hover:bg-canvas"
            >
              {t.customise}
            </button>
          ) : (
            <button
              onClick={() => save(analytics, marketing)}
              className="rounded-lg border border-brand-600 px-4 py-2 text-sm font-medium text-brand-ink hover:bg-brand-50"
            >
              {t.saveChoice}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
